import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FiActivity,
  FiAlertCircle,
  FiBriefcase,
  FiCalendar,
  FiDollarSign,
  FiFileText,
  FiPackage,
  FiRefreshCw,
  FiTrendingUp,
  FiTruck,
  FiUsers
} from 'react-icons/fi';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { API_ENDPOINTS } from '../../utils/api';
import { fetchAllInvoices } from '../../utils/zovcurInvoiceApi';
import { formatINR } from '../../utils/currency';
import { buildInvoiceStats, formatRelativeTime } from '../../utils/companyOverviewUtils';
import { COMPANY_DEFAULTS, computeInvoiceTotals } from '../../utils/zovcurInvoiceStore';

const CHART_COLORS = ['#0d9488', '#1a365d', '#f59e0b', '#ef4444', '#6366f1'];

const StatCard = ({ label, value, sub, icon: Icon, tone = 'navy' }) => {
  const tones = {
    navy: 'text-[#1a365d] bg-[#e8f0f4]',
    teal: 'text-teal-700 bg-teal-50',
    emerald: 'text-emerald-700 bg-emerald-50',
    amber: 'text-amber-700 bg-amber-50',
    rose: 'text-rose-700 bg-rose-50',
    indigo: 'text-indigo-700 bg-indigo-50'
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-slate-500">
            {label}
          </p>
          <p className="mt-1 text-xl sm:text-2xl font-bold text-slate-800 truncate">{value}</p>
          {sub ? <p className="text-[10px] sm:text-xs text-slate-400 mt-1 truncate">{sub}</p> : null}
        </div>
        <div className={`shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon className="text-base sm:text-lg" />
        </div>
      </div>
    </div>
  );
};

const QuickAction = ({ label, onClick, tone = 'teal' }) => {
  const tones = {
    teal: 'bg-teal-50 hover:bg-teal-100 text-teal-800 border-teal-100',
    navy: 'bg-[#e8f0f4] hover:bg-[#dbe8ef] text-[#1a365d] border-slate-200',
    amber: 'bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-100',
    emerald: 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-100'
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-3 rounded-xl border font-medium text-sm transition-colors ${tones[tone]}`}
    >
      {label}
    </button>
  );
};

const CompanyOverview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companySettings, setCompanySettings] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [stockTransactions, setStockTransactions] = useState([]);
  const [expiryStats, setExpiryStats] = useState({ total: 0, expiring: 0, expired: 0 });
  const [attendanceSummary, setAttendanceSummary] = useState(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const loadOverview = useCallback(async () => {
    setLoading(true);
    const headers = authHeaders();

    const results = await Promise.allSettled([
      axios.get(API_ENDPOINTS.getUsers, { headers }),
      axios.get(API_ENDPOINTS.getCompanySettings, { headers }),
      fetchAllInvoices(),
      axios.get(API_ENDPOINTS.getSalesmen, { headers }),
      axios.get(API_ENDPOINTS.getDistributors, { headers }),
      axios.get(API_ENDPOINTS.getStockTransactions, { headers }),
      axios.get(API_ENDPOINTS.getExpiryTracking, { headers }),
      axios.get(API_ENDPOINTS.getAdminSummary, { headers })
    ]);

    if (results[0].status === 'fulfilled') {
      setEmployees(Array.isArray(results[0].value.data) ? results[0].value.data : []);
    } else setEmployees([]);

    if (results[1].status === 'fulfilled') {
      setCompanySettings(results[1].value.data || null);
    } else setCompanySettings(null);

    if (results[2].status === 'fulfilled') {
      setInvoices(results[2].value || []);
    } else setInvoices([]);

    if (results[3].status === 'fulfilled') {
      setSalesmen(results[3].value.data || []);
    } else setSalesmen([]);

    if (results[4].status === 'fulfilled') {
      setDistributors(results[4].value.data || []);
    } else setDistributors([]);

    if (results[5].status === 'fulfilled') {
      setStockTransactions(results[5].value.data || []);
    } else setStockTransactions([]);

    if (results[6].status === 'fulfilled') {
      const expiryData = results[6].value.data || {};
      setExpiryStats(expiryData.stats || { total: 0, expiring: 0, expired: 0 });
    } else setExpiryStats({ total: 0, expiring: 0, expired: 0 });

    if (results[7].status === 'fulfilled') {
      setAttendanceSummary(results[7].value.data || null);
    } else setAttendanceSummary(null);

    setLoading(false);
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const invoiceStats = useMemo(() => buildInvoiceStats(invoices), [invoices]);

  const companyName =
    companySettings?.companyName || COMPANY_DEFAULTS.name;
  const companyAddress =
    companySettings?.companyAddress || COMPANY_DEFAULTS.address;
  const companyPhone =
    companySettings?.companyPhone || COMPANY_DEFAULTS.phone;

  const presentToday = attendanceSummary?.presentToday ?? '—';
  const totalEmployees = employees.length;

  const statCards = [
    {
      label: 'Employees',
      value: totalEmployees,
      sub: `${presentToday} present today`,
      icon: FiUsers,
      tone: 'navy'
    },
    {
      label: 'Salesmen',
      value: salesmen.length,
      sub: `${invoiceStats.topSalesmen.length} active in invoices`,
      icon: FiBriefcase,
      tone: 'teal'
    },
    {
      label: 'Total Invoices',
      value: invoiceStats.totalInvoices,
      sub: `${invoiceStats.thisMonthInvoices} this month`,
      icon: FiFileText,
      tone: 'indigo'
    },
    {
      label: 'Monthly Revenue',
      value: formatINR(invoiceStats.monthlyRevenue),
      sub: `Total ${formatINR(invoiceStats.totalRevenue)}`,
      icon: FiDollarSign,
      tone: 'emerald'
    },
    {
      label: 'Strips Sold',
      value: invoiceStats.totalStrips.toLocaleString('en-IN'),
      sub: 'From all invoices',
      icon: FiPackage,
      tone: 'amber'
    },
    {
      label: 'Distributors',
      value: distributors.length,
      sub: 'Registered partners',
      icon: FiTruck,
      tone: 'teal'
    },
    {
      label: 'Unpaid Invoices',
      value: invoiceStats.unpaidCount,
      sub: formatINR(invoiceStats.unpaidAmount),
      icon: FiAlertCircle,
      tone: 'rose'
    },
    {
      label: 'Stock Movements',
      value: stockTransactions.length,
      sub: `${expiryStats.expiring || 0} items expiring soon`,
      icon: FiActivity,
      tone: 'navy'
    }
  ];

  return (
    <div className="p-4 md:p-6 bg-gradient-to-br from-slate-100 via-teal-50/30 to-slate-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-3">
              <FiBriefcase className="text-teal-600" />
              Company Overview
            </h1>
            <p className="text-slate-600 mt-1">{companyName} — live business metrics</p>
            {(companyAddress || companyPhone) && (
              <p className="text-xs text-slate-500 mt-1">
                {[companyAddress, companyPhone && `Phone: ${companyPhone}`].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={loadOverview}
            className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-2"
          >
            <FiRefreshCw /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600" />
            <p className="text-slate-500 mt-3 text-sm">Loading company data…</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
              {statCards.map((stat) => (
                <StatCard key={stat.label} {...stat} />
              ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
              <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5">
                <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <FiTrendingUp className="text-teal-600" />
                  Monthly Revenue Trend
                </h2>
                {invoiceStats.monthlyTrend.length === 0 ? (
                  <div className="h-56 flex items-center justify-center text-slate-400 text-sm">
                    No invoice data yet
                  </div>
                ) : (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={invoiceStats.monthlyTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                        <Tooltip
                          formatter={(value, name) => [
                            name === 'amount' ? formatINR(value) : value,
                            name === 'amount' ? 'Revenue' : 'Strips'
                          ]}
                        />
                        <Bar dataKey="amount" fill="#0d9488" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5">
                <h2 className="font-bold text-slate-800 mb-4">Payment Status</h2>
                {invoiceStats.totalInvoices === 0 ? (
                  <div className="h-56 flex items-center justify-center text-slate-400 text-sm">
                    No invoices yet
                  </div>
                ) : (
                  <>
                    <div className="h-44">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={invoiceStats.paymentBreakdown.filter((d) => d.value > 0)}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={45}
                            outerRadius={70}
                            paddingAngle={3}
                          >
                            {invoiceStats.paymentBreakdown.map((entry, index) => (
                              <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-emerald-700 font-medium">Paid</span>
                        <span>
                          {invoiceStats.paidCount} · {formatINR(invoiceStats.paidAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-rose-700 font-medium">Unpaid</span>
                        <span>
                          {invoiceStats.unpaidCount} · {formatINR(invoiceStats.unpaidAmount)}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200">
                  <h2 className="font-bold text-slate-800 flex items-center gap-2">
                    <FiCalendar className="text-teal-600" />
                    Recent Invoices
                  </h2>
                </div>
                {invoiceStats.recentInvoices.length === 0 ? (
                  <div className="p-8 text-center text-slate-500 text-sm">No invoices yet</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b">
                          <th className="px-4 py-3 text-left">Invoice</th>
                          <th className="px-4 py-3 text-left">Doctor</th>
                          <th className="px-4 py-3 text-left">Salesman</th>
                          <th className="px-4 py-3 text-right">Amount</th>
                          <th className="px-4 py-3 text-left">Status</th>
                          <th className="px-4 py-3 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceStats.recentInvoices.map((inv) => {
                          const amount = computeInvoiceTotals(inv?.products || []).roundedNetAmount;
                          return (
                            <tr key={inv.id || inv._id} className="border-b hover:bg-slate-50">
                              <td className="px-4 py-3 font-medium text-indigo-700">
                                {inv.invoiceNumber}
                              </td>
                              <td className="px-4 py-3">{inv.customer?.doctor || '—'}</td>
                              <td className="px-4 py-3">
                                {String(inv.invoiceDetails?.salesmanName || '').trim() || '—'}
                              </td>
                              <td className="px-4 py-3 text-right font-medium">
                                {formatINR(amount)}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    inv.paymentStatus === 'Paid'
                                      ? 'bg-emerald-50 text-emerald-700'
                                      : 'bg-amber-50 text-amber-700'
                                  }`}
                                >
                                  {inv.paymentStatus || 'Unpaid'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-slate-500 text-xs">
                                {formatRelativeTime(
                                  inv.invoiceDetails?.billDate || inv.createdAt
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5">
                  <h2 className="font-bold text-slate-800 mb-4">Top Salesmen</h2>
                  {invoiceStats.topSalesmen.length === 0 ? (
                    <p className="text-sm text-slate-500">No salesman data from invoices</p>
                  ) : (
                    <div className="space-y-3">
                      {invoiceStats.topSalesmen.map((row, index) => (
                        <div key={row.name} className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center text-xs font-bold">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{row.name}</p>
                            <p className="text-xs text-slate-500">
                              {row.strips} strips · {row.invoices} invoices
                            </p>
                          </div>
                          <p className="text-sm font-semibold text-[#1a365d]">
                            {formatINR(row.amount)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5">
                  <h2 className="font-bold text-slate-800 mb-4">Quick Actions</h2>
                  <div className="space-y-2">
                    <QuickAction label="Create New Invoice" onClick={() => navigate('/invoices')} tone="navy" />
                    <QuickAction label="View Orders & Payments" onClick={() => navigate('/orders')} tone="teal" />
                    <QuickAction label="Salesman Performance" onClick={() => navigate('/user-performance')} tone="emerald" />
                    <QuickAction label="Set Sales Targets" onClick={() => navigate('/sales-target-reports')} tone="amber" />
                    <QuickAction label="Stock In / Out" onClick={() => navigate('/stock-in-out')} tone="teal" />
                    <QuickAction label="Expiry Tracking" onClick={() => navigate('/expiry-tracking')} tone="rose" />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompanyOverview;
